import * as d3 from "d3";

const formatTime = d3.timeFormat("%b %d, %Y");
const parseDate = d3.timeParse("%m/%d/%Y");

export function draw(container, data, config, setHoveredLabel) {
  const cwidth = container.clientWidth;
  const cheight = container.clientHeight;

  const {
    axisLines,
    radialLines,
    featureAlias,
    featureStyle,
    header,
    layout,
    legend,
    lineStyle,
    svg: svgx,
  } = config;

  const featureAliasArray = Object.entries(featureAlias)
    .sort((a, b) => (+a[0] > +b[0] ? 1 : +a[0] < +b[0] ? -1 : 0))
    .map((item) => item[1]);

  const svg = d3.select(container);
  svg.selectAll("g").remove();

  if (data.data) {
    /** d3 margin convention */
    const width = cwidth - layout.margin.left - layout.margin.right;
    const height = cheight - layout.margin.top - layout.margin.bottom;
    /** SVG Styles */
    svg.style("background-color", svgx.Background).on("d");
    if (svgx.border) {
      svg.style("border", svgx.border);
    }

    if (data.header) {
      const headerGroup = svg.append("g");
      headerGroup
        .append("text")
        .attr("y", header.margin.top)
        .attr("x", cwidth / 2)
        .attr("fill", header.colour)
        .style("text-anchor", "middle")
        .style("font-size", header.fontSize)
        .style("font-weight", header.fontWeight)
        .text(data.header);
    }
    if (data.legend && legend.active) {
      const legendGroup = svg
        .append("g")
        .attr("transform", `translate(${0},${legend.margin.top})`)
        .append("foreignObject")
        .attr("x", 0)
        .attr("y", 0)
        .attr("width", cwidth)
        .attr("height", 50);

      const legendContainer = legendGroup
        .append("xhtml:div")
        .style("display", "flex")
        .style("align-items", "center")
        .style("justify-content", "center")
        .style("width", "100%")
        .style("height", "100%");

      const legendItems = legendContainer
        .selectAll(".legendItem")
        .data(data.legend)
        .enter()
        .append("xhtml:div")
        .style("display", "flex")
        .style("justify-content", "center");

      const hexToRgb = (hex) => {
        var result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
        return result
          ? {
              r: parseInt(result[1], 16),
              g: parseInt(result[2], 16),
              b: parseInt(result[3], 16),
            }
          : null;
      };

      legendItems
        .append("xhtml:div")
        .attr("class", "lcont")
        .style("display", "flex")
        .style("align-items", "center")
        .style("margin-right", "10px")
        .style("margin-left", "10px")
        .append("xhtml:div")
        .style("width", `${legend.lineWidth}px`)
        .style("height", (d, i) => {
          return lineStyle.fill.colours[i] !== "none"
            ? "100%"
            : `${legend.lineHeight}px`;
        })
        .style("border-top", (_, i) =>
          lineStyle.fill.colours[i] !== "none"
            ? undefined
            : `${legend.lineHeight}px ${
                config.lineStyle.dashed[i] === "" ? "solid" : "dashed"
              } ${config.lineStyle.colours[i]}`
        )
        .style("background-color", (d, i) => {
          if (lineStyle.fill.colours[i] === "none") {
            return "none";
          } else {
            const rgbValues = Object.values(
              hexToRgb(lineStyle.fill.colours[i])
            );
            rgbValues.push(lineStyle.fill.opacity[i]);
            return `rgba(${rgbValues.join(",")})`;
          }
        });

      legendItems
        .append("xhtml:div")
        .style("color", legend.colour)
        .style("font-size", legend.fontSize)
        .style("font-weight", legend.fontWeight)
        .html((d, i) => d[1] + "_" + formatTime(parseDate(d[0])));
    }

    const maxValue = Math.max(
      ...data.data.reduce((res, row) => {
        return res.concat(Object.values(row));
      }, [])
    );

    const tickfactor = maxValue / 5;

    let radialScale = d3
      .scaleLinear()
      .domain([0, maxValue])
      .range([0, Math.min(height, width) / 2]);

    let ticks = [1, 2, 3, 4, 5].map((item) => {
      return item * tickfactor;
    });

    const chartGroup = svg
      .append("g")
      .attr(
        "transform",
        `translate (${layout.margin.left},${layout.margin.top})`
      );

    const circleGroup = chartGroup.append("g");
    const axislineGroup = chartGroup.append("g");
    const axislabelGroup = chartGroup.append("g");
    const pathGroup = chartGroup.append("g");

    circleGroup
      .selectAll("circle")
      .data(ticks)
      .enter()
      .append("circle")
      .attr("cx", width / 2)
      .attr("cy", height / 2)
      .attr("fill", "none")
      .attr("stroke", radialLines.colour)
      .attr("r", (d) => radialScale(d));

    function angleToCoordinate(angle, value) {
      let x = Math.cos(angle) * radialScale(value);
      let y = Math.sin(angle) * radialScale(value);
      return { x: width / 2 + x, y: height / 2 - y };
    }
    const getFeaturedata = () => {
      return featureAliasArray.map((f, i) => {
        let angle = Math.PI / 2 + (2 * Math.PI * i) / featureAliasArray.length;
        return {
          name: f,
          angle: angle,
          line_coord: angleToCoordinate(angle, maxValue),
          label_coord: angleToCoordinate(angle, maxValue + maxValue / 20),
        };
      });
    };

    // draw axis line
    axislineGroup
      .selectAll(".line")
      .data(getFeaturedata())
      .enter()
      .append("line")
      .attr("x1", width / 2)
      .attr("y1", height / 2)
      .attr("x2", (d) => d.line_coord.x)
      .attr("y2", (d) => d.line_coord.y)
      .attr("stroke", axisLines.colour);

    // draw axis label
    axislabelGroup
      .selectAll(".axislabel")
      .data(getFeaturedata())
      .enter()
      .append("text")
      .style("font-size", featureStyle.fontSize)
      .style("font-weight", featureStyle.fontWeight)
      .attr("x", (d) => d.label_coord.x)
      .attr("y", (d) => d.label_coord.y)
      .style("text-anchor", (d) =>
        d.label_coord.x > width / 2
          ? "start"
          : d.label_coord.x < width / 2
          ? "end"
          : "middle"
      )
      .text((d) => d.name)
      .on("mouseover", function (e, d) {
        const element = d3.select(this);
        element.style("font-weight", featureStyle.hoverWeight);
        setHoveredLabel({ label: d.name, coord: d.label_coord });
      })
      .on("mouseout", function (e) {
        const element = d3.select(this);
        element.style("font-weight", featureStyle.fontWeight);
        setHoveredLabel(null);
      });

    let line = d3
      .line()
      .x((d) => d.x)
      .y((d) => d.y);

    function getPathCoordinates(data_point, i) {
      let coordinates = [];
      for (var j = 0; j < data.features[i].length; j++) {
        let ft_name = data.features[i][j];
        let angle = Math.PI / 2 + (2 * Math.PI * j) / data.features[i].length;
        coordinates.push(angleToCoordinate(angle, data_point[ft_name]));
      }
      //close the line
      coordinates.push(coordinates[0]);
      return coordinates;
    }

    // draw the path element
    pathGroup
      .selectAll("path")
      .data(data.data)
      .enter()
      .append("path")
      .datum((d, i) => getPathCoordinates(d, i))
      .attr("d", line)
      .attr("stroke-width", (d, i) => lineStyle.strokeWidth[i])
      .attr("stroke", (_, i) => lineStyle.colours[i])
      .style("stroke-dasharray", (_, i) =>
        lineStyle.dashed[i] === "" ? undefined : lineStyle.dashed[i]
      )
      .attr("fill", (d, i) => lineStyle.fill.colours[i])
      .attr("stroke-opacity", 1)
      .attr("fill-opacity", (d, i) => lineStyle.fill.opacity[i])
      .style("cursor", "pointer");
  }
}
